/**
 * InfraSys Storage Inventory - React Application
 *
 * Storage-inventory web app for the Live Infrastructure team. Speaks the
 * backend-agnostic /inventory/* contract: in production it is served by
 * API Gateway -> Lambda backed by DynamoDB (assets + settings) and S3 (photos);
 * in local dev it is served by the dev proxy.
 */
import { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import { Navigation } from './components/Navigation';
import { StorageInventory } from './pages/StorageInventory';
import { StorageInventoryDev } from './pages/StorageInventoryDev';
import { useCurrentUser } from './hooks/useCurrentUser';
import './App.css';
import './styles/logo.css';
import './styles/accessibility.css';
import './styles/spacing.css';
import './styles/transitions.css';

function App() {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const currentUser = useCurrentUser();

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* ARIA live region for screen reader announcements */}
      <div id="aria-live-region" role="status" aria-live="polite" aria-atomic="true" />

      {/* Brand bar: Prime logo (top-left) + header / subheader */}
      <header className="brand-bar">
        <a className="brand-left" href="/storage-inventory" aria-label="InfraSys Storage Inventory home" style={{ textDecoration: 'none' }}>
          <img className="brand-logo" src="/prime-logo.png" alt="Prime Video" />
          <span className="brand-text">
            <span className="brand-title">InfraSys Storage Inventory</span>
            <span className="brand-subtitle">Live Infrastructure - Offline Storage Inventory</span>
          </span>
        </a>
        <div className="brand-right">
          {currentUser && <span className="brand-user">{currentUser}</span>}
        </div>
      </header>

      <AppLayout
        navigation={<Navigation />}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        toolsHide
        maxContentWidth={Number.MAX_VALUE}
        content={
          <main id="main-content" role="main">
            <Routes>
              <Route path="/" element={<Navigate to="/storage-inventory" replace />} />
              <Route path="/storage-inventory" element={<StorageInventory />} />
              <Route path="/storage-inventory/dev" element={<StorageInventoryDev />} />
              <Route path="*" element={<Navigate to="/storage-inventory" replace />} />
            </Routes>
          </main>
        }
      />
    </>
  );
}

export default App;
